import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  UserIcon,
  ArrowLeftIcon,
  PencilIcon,
  CalendarIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Avatar from '../components/Avatar.tsx';
import { userService } from '../services/userService.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteMutation = useMutation(
    () => userService.deleteUser(Number(id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        navigate('/users');
      },
    }
  );

  // Fetch user data
  const { data: user, isLoading, error } = useQuery(
    ['user', id],
    () => userService.getUserById(Number(id!)),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 0, // Sempre considerar os dados como stale
      cacheTime: 0, // Não manter cache
    }
  );

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
      case 'director':
        return 'Administrador';
      case 'user':
      case 'operations':
      case 'finance':
      case 'it':
        return 'Usuário';
      default:
        return 'Usuário';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
      case 'director':
        return 'bg-purple-100 text-purple-800';
      case 'user':
      case 'operations':
      case 'finance':
      case 'it':
        return 'bg-primary-100 text-primary-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Usuário não encontrado</h2>
          <p className="text-gray-600 mb-4">O usuário solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/users')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
          >
            Voltar para Usuários
          </button>
        </div>
      </div>
    );
  }

  const isSelf = currentUser && String(currentUser.id) === String(user.id);

  return (
    <div>
      <div className="breadcrumb" style={{ marginBottom: 4 }}>Config / Usuários / {user.name}</div>
      <h1 className="page-title">{user.name}</h1>
      <div className="page-subtitle" style={{ marginBottom: 8 }}>{user.email}</div>
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div className="filters">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/users')}>
            ← Voltar para Usuários
          </button>
        </div>
        <div className="detail-actions">
          {hasPermission('users.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/users/edit/${user.id}`)}
            >
              Editar
            </button>
          )}
          {hasPermission('users.delete') && !isSelf && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </div>
      </div>
      <div>

        {/* User Info Card */}
        <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-8">
            {/* Profile Section */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
              <div className="flex-shrink-0">
                <Avatar name={user.name} size="xl" src={user.avatar} />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{user.name}</h2>
                <p className="text-gray-600 mb-4 flex items-center justify-center sm:justify-start">
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  {user.email}
                </p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(
                      user.role
                    )}`}
                  >
                    <ShieldCheckIcon className="h-4 w-4 mr-1" />
                    {getRoleLabel(user.role)}
                  </span>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      user.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {user.active ? (
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 mr-1" />
                    )}
                    {user.active ? 'Ativo' : 'Inativo'}
                  </span>
                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-slate-100 text-slate-800">
                    MFA {user.mfaEnabled ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Account Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Informações da Conta
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo
                    </label>
                    <p className="text-gray-900">{user.name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <p className="text-gray-900">{user.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Função
                    </label>
                    <p className="text-gray-900">{getRoleLabel(user.role)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <p className={`font-medium ${user.active ? 'text-green-600' : 'text-red-600'}`}>
                      {user.active ? 'Ativo' : 'Inativo'}
                    </p>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Informações do Sistema
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Criação
                    </label>
                    <p className="text-gray-900">
                      {new Date(user.createdAt).toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Última Atualização
                    </label>
                    <p className="text-gray-900">
                      {new Date(user.updatedAt).toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID do Usuário
                    </label>
                    <p className="text-gray-900 font-mono text-sm">{user.id}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
};

export default UserDetail;